// src/components/sections/EtTrinityAdvantage.jsx
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LightBulbIcon,
  FlagIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

export default function EtTrinityAdvantage({onNext}) {
  // First Created - 15 Aug 2025
  // Author - Atul Pandey
  // Updated - 31 Oct 2025 - Step 3a: introspective questions + contextual summary
  // Updated - 01 Nov 2025 - Step 3a: local storage - questions + contextual summary
  // Updated - 02 Nov 2025 - Add user defined prompt chips

  const [thinkingSelected, setThinkingSelected] = useState([]);
  const [purposeSelected, setPurposeSelected] = useState([]);
  const [habitsSelected, setHabitsSelected] = useState([]);

  // Derived visibility
  const showPurpose = thinkingSelected.length > 0;
  const showHabits = showPurpose && purposeSelected.length > 0;
  const showSummary = showHabits && habitsSelected.length > 0;

  return (
    <section id="advantage" className="py-20 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdvantageIntro />

        {/* Thinking, Purpose, Habits grid layout */}
        <div
          className={`${
            showSummary
              ? "grid md:grid-cols-3 gap-6 items-stretch"
              : "flex flex-col"
          }`}
        >
        {/* Thinking */}
        <PromptCard
          icon={LightBulbIcon}
          title="Thinking → Clarity over Chaos"
          subtitle="When you think about growth this quarter, your mind feels…"
          chips={[
            "Pulled in 10 directions",
            "Reactive firefighting",
            "Clear but scattered",
            "Mostly focused",
            "Crystal clear",
          ]}
          selected={thinkingSelected}
          onSelect={setThinkingSelected}
        />

        {/* Purpose */}
        <AnimatePresence>
          {showPurpose && (
            <motion.div
              key="purpose"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              // className="mt-6"
              className={`${showSummary ? "" : "mt-6"}`}
            >
              <PromptCard
                icon={FlagIcon}
                // title="Purpose → Center before Speed"
                title="Purpose → Focus before Speed"
                subtitle="Who is at the center of your current growth thinking?"
                chips={[
                  "Self",
                  "Team",
                  "Clients",
                  "Partners",
                  "Investor",
                  "All—no clear center",
                ]}
                selected={purposeSelected}
                onSelect={setPurposeSelected}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Habits */}
        <AnimatePresence>
          {showHabits && (
            <motion.div
              key="habits"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              // className="mt-6"
              className={`${showSummary ? "" : "mt-6"}`}
            >
              <PromptCard
                icon={PencilSquareIcon}
                title="Habits → Publishing your Mind"
                subtitle="Which habits feel under-developed right now?"
                chips={[
                  "Writing (reflections, communicating)",
                  "Speaking (influence)",
                  "Listening (discovery)",
                  "Reading (depth)",
                  "Publishing (IP/blogs)",
                  "Cadence (weekly reviews)",
                ]}
                selected={habitsSelected}
                onSelect={setHabitsSelected}
              />
            </motion.div>
          )}
        </AnimatePresence>
        </div>


        {/* Summary */}
        <AnimatePresence>
          {showSummary && (
            <motion.div
              key="summary"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              // className="mt-10"
              className="mt-16 md:mt-20"
            >
              <SummaryCard
                thinking={thinkingSelected}
                purpose={purposeSelected}
                habits={habitsSelected}
                onNext={onNext}
                // 01 Nov 2025
                thinkingSelected={thinkingSelected}
                purposeSelected={purposeSelected}
                habitsSelected={habitsSelected}
              />
            </motion.div>
          )}
        </AnimatePresence>
        


      </div>
    </section>
  );
}

/* ---------- Helper: Reflection Question Generator ---------- */
function generateReflectionQuestions({ thinking, purpose, habits }) {
  // First Created - 31 Oct 2025
  // Author - Atul Pandey

  const pick = (arr, key) => arr.find((x) => key.includes(x));

  // THINKING
  let thinkingQ =
    "What helps you quiet the noise and find clarity before acting?";
  if (pick(thinking, "Pulled") || pick(thinking, "Reactive"))
    thinkingQ =
      "When everything feels urgent, how do you decide what truly deserves your attention?";
  else if (pick(thinking, "Clear") || pick(thinking, "Focused"))
    thinkingQ =
      "What keeps your clarity steady when the pace inevitably picks up?";

  // PURPOSE
  let purposeQ = "Whose progress defines success for you right now?";
  if (pick(purpose, "Self"))
    purposeQ =
      "If your goals center on yourself, what would shift if they centered on others?";
  else if (pick(purpose, "All") || pick(purpose, "no clear"))
    purposeQ =
      "When everything feels equally important, what’s the single purpose that anchors you?";
  else if (pick(purpose, "Client") || pick(purpose, "Partner"))
    purposeQ =
      "How well do your clients or partners feel your purpose in their success?";

  // HABITS
  let habitsQ = "Which one practice could most amplify your intent?";
  if (pick(habits, "Writing"))
    habitsQ =
      "What truth about your work deserves to be written down more often?";
  else if (pick(habits, "Speaking"))
    habitsQ = "When was the last time your words truly shifted a room?";
  else if (pick(habits, "Listening"))
    habitsQ =
      "What conversation might change everything if you listened without preparing a reply?";
  else if (pick(habits, "Publishing"))
    habitsQ =
      "What insight of yours could ripple wider if you shared it publicly?";
  else if (pick(habits, "Cadence"))
    habitsQ =
      "What weekly ritual would remind you that growth is a rhythm, not a race?";

  return { thinkingQ, purposeQ, habitsQ };
}

/* ---------- Subcomponents ---------- */

function AdvantageIntro() {
  // First Created - 31 Oct 2025
  // Author - Atul Pandey

  return (
    <div className="text-center">
      <motion.h2
        className="text-3xl md:text-4xl font-bold mb-4 text-orange-500"
        initial={{ opacity: 0, y: -16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        Where does your growth thinking stand today?
      </motion.h2>

      <motion.p
        className="text-gray-300 max-w-3xl mx-auto text-lg leading-relaxed"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        Before every leap, comes a pause for clarity. Take a moment to reflect on
        how you <span className="text-orange-400">think</span>,
        <span className="text-orange-400"> focus</span>, and
        <span className="text-orange-400"> practice</span> growth today. This brief
        reflection will help you see where your next shift begins.
      </motion.p>
    </div>
  );
}

function PromptCard({ icon: Icon, title, subtitle, chips, selected, onSelect }) {
  // First Created - 31 Oct 2025
  // Author - Atul Pandey
  // Updated - 02 Nov 2025 - user defined prompt chips


  // 1 - State variable - 02 Nov 2025
  const [customInput, setCustomInput] = useState("");

  // 2 - Toggle Chip
  const toggleChip = (chip) => {
    onSelect((prev) =>
      prev.includes(chip)
        ? prev.filter((c) => c !== chip)
        : [...prev, chip]
    );
  };

  // 3 - Add Custom - 02 Nov 2025
  const handleAddCustom = () => {
    const trimmed = customInput.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onSelect((prev) => [...prev, trimmed]);
      setCustomInput("");
    }
  };

  return (
    <motion.div
      className="rounded-2xl bg-white/5 border border-white/10 p-6 shadow-lg my-10 h-full flex flex-col"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-3 mb-4">
        <Icon className="h-7 w-7 text-orange-500" />
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-gray-300 mb-4">{subtitle}</p>

      {/* <div className="flex flex-wrap gap-2">
        {chips.map((chip) => {
          const active = selected.includes(chip);
          return (
            <button
              key={chip}
              onClick={() => toggleChip(chip)}
              className={`text-sm px-3 py-1 rounded-full border transition ${
                active
                  ? "bg-orange-600 border-orange-500 text-white"
                  : "bg-white/10 border-white/10 text-gray-200 hover:bg-white/20"
              }`}
            >
              {chip}
            </button>
          );
        })}
      </div> */}

      {/* 02 Nov 2025 - With user defined options */}
      <div className="flex flex-wrap gap-2 mb-4">
        {chips.map((chip) => {
          const active = selected.includes(chip);
          return (
            <button
              key={chip}
              onClick={() => toggleChip(chip)}
              className={`text-sm px-3 py-1 rounded-full border transition ${
                active
                  ? "bg-orange-600 border-orange-500 text-white"
                  : "bg-white/10 border-white/10 text-gray-200 hover:bg-white/20"
              }`}
            >
              {chip}
            </button>
          );
        })}
        {/* Show custom chips (user-added, toggleable) */}
        {selected
          .filter((chip) => !chips.includes(chip))
          .map((chip) => (
            <button
              key={chip}
              onClick={() => toggleChip(chip)}
              className={`text-sm px-3 py-1 rounded-full border transition ${
                selected.includes(chip)
                  ? "bg-orange-600 border-orange-500 text-white"
                  : "bg-white/10 border-white/10 text-gray-200 hover:bg-white/20"
              }`}
            >
              {chip}
            </button>
          ))}

        </div>

        {/* Add custom input */}
        {/* <div className="flex items-center gap-2"> */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
          <input
            type="text"
            placeholder="Add your own..."
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustom())}
            className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-600"
          />
          <button
            onClick={handleAddCustom}
            // className="px-3 py-2 rounded-lg bg-orange-600 text-white text-sm hover:bg-orange-700 transition"
            className="shrink-0 px-3 py-2 rounded-lg bg-orange-600 text-white text-sm hover:bg-orange-700 transition"
          >
            +
          </button>
        </div>

    </motion.div>
  );
}

function SummaryCard({ 
  thinking, 
  purpose, 
  habits, 
  onNext,
  // 01 Nov 2025
  thinkingSelected,
  purposeSelected,
  habitsSelected, 

}) {
  const { thinkingQ, purposeQ, habitsQ } = generateReflectionQuestions({
    thinking,
    purpose,
    habits,
  });

  return (
    <motion.div
      className="rounded-2xl bg-gradient-to-b from-zinc-900 to-black border border-white/10 p-6 md:p-8"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="mb-6 text-center">
        <h4 className="text-xl font-semibold text-orange-400">
          Your Trinity Reflection
        </h4>
        <p className="text-sm text-gray-400 font-light">
          Here are some seed questions to introspect before we go further.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400">
            Thinking
          </p>
          <p className="text-sm text-gray-200 mt-2 italic">“{thinkingQ}”</p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400">
            Purpose
          </p>
          <p className="text-sm text-gray-200 mt-2 italic">“{purposeQ}”</p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400">
            Habits
          </p>
          <p className="text-sm text-gray-200 mt-2 italic">“{habitsQ}”</p>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="px-6 py-3 bg-orange-600 text-white rounded-xl font-semibold shadow hover:bg-orange-700 transition"
          onClick={() => {
            // 01 Nov 2025
            const reflection = {
              growthDefinition: localStorage.getItem("trinity.hero.growthDefinition") || "",
              thinkingSelected,
              purposeSelected,
              habitsSelected,
              reflectionQuestions: {
                thinkingQ,
                purposeQ,
                habitsQ,
              },
            };

            localStorage.setItem("trinity.reflectionData", JSON.stringify(reflection));

            // ✅ New line to capture reflection end time
            sessionStorage.setItem("trinity.endTime", Date.now().toString());

            document
              .getElementById("contact")
              ?.scrollIntoView({ behavior: "smooth" })
            
              onNext?.();
            
          }}
        >
          Continue This Reflection →
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="px-6 py-3 bg-white/10 text-orange-400 rounded-xl font-semibold border border-white/10 hover:bg-white/20 transition"
          onClick={() => {

             // 25 Nov 2025
            const reflection = {
              growthDefinition: localStorage.getItem("trinity.hero.growthDefinition") || "",
              thinkingSelected,
              purposeSelected,
              habitsSelected,
              reflectionQuestions: {
                thinkingQ,
                purposeQ,
                habitsQ,
              },
            };

            localStorage.setItem("trinity.reflectionData", JSON.stringify(reflection));

            // ✅ New line to capture reflection end time
            sessionStorage.setItem("trinity.endTime", Date.now().toString());

             document
              .getElementById("approach") // 👈 or use "how-it-works" if that's your section ID
              ?.scrollIntoView({ behavior: "smooth" })

            onNext?.();
           
          }}
        >
          Next: Trinity Approach →
        </motion.button>
      </div>

    </motion.div>
  );
}